/**
 * Shared helpers + small UI primitives for the ProjectBuilder tab files.
 * Kept tiny on purpose — only what's used by ≥2 tabs lives here.
 */
import { API } from "@/lib/api";

export const LOAD_TYPES = ["motor", "lighting", "heat_trace", "hvac", "receptacle", "general"];

/* Auto-tag prefix by equipment category / type. */
export const TAG_PREFIX = {
  vfd: "VFD", motor: "M", transformer: "XFMR", panelboard: "PNL",
  mcc: "MCC", disconnect: "DS", pump: "PMP", heater: "HTR",
  heat_trace_controller: "HTC", lighting: "LTG", receptacle: "RCP",
  contactor: "CON", breaker: "BKR", relay: "RLY", plc: "PLC",
  enclosure: "ENC", hvac: "HVAC",
};

function _prefixFor(equipment_type) {
  const t = String(equipment_type || "").toLowerCase();
  for (const k of Object.keys(TAG_PREFIX)) if (t.includes(k)) return TAG_PREFIX[k];
  return "EQ";
}

/** Next available tag like "VFD-001" (Pump, Motor, VFD, etc). */
export function nextTagFor(equipment_type, loadList) {
  const prefix = _prefixFor(equipment_type);
  const used = new Set((loadList || []).map((r) => String(r.tag || "").toUpperCase()));
  for (let n = 1; n < 1000; n += 1) {
    const candidate = `${prefix}-${String(n).padStart(3, "0")}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${prefix}-${Date.now().toString().slice(-4)}`;
}

export function newCircuit() {
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()),
    tag: "",
    description: "",
    load_type: "general",
    voltage: 480,
    phases: 3,
    load_amps: 0,
    breaker_amps: 0,
    poles: 3,
    notes: "",
  };
}

/** Authenticated blob download (used by CSV / XLSX / PDF / DXF exports). */
export async function downloadFile(url, filename) {
  const resp = await fetch(`${API}${url}`, { credentials: "include" });
  if (!resp.ok) throw new Error("download failed");
  const blob = await resp.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
}

/** Label/field wrapper used by every form input across the tabs. */
export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-tag block mb-1">{label}</span>
      {children}
    </label>
  );
}

/** Stat tile used by SizeTab + CablesTab summary rows. */
export function SummaryStat({ label, value, testid, accent = false }) {
  return (
    <div className="border border-white/10 bg-[#0d1015] p-3" data-testid={testid}>
      <div className="label-tag mb-1">{label}</div>
      <div className={`font-display text-xl ${accent ? "text-[#00E5FF]" : "text-white"}`}>{value}</div>
    </div>
  );
}
